import { DashboardRecordDto } from 'libs/end-to-end/src/dashboard';
import { DraftDashboardChart } from 'libs/end-to-end/src/dashboard';
import { DraftDashboard } from 'libs/end-to-end/src/dashboard';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { CustomVizId } from 'libs/end-to-end/src/custom-viz/CustomVizId';
import { copyObjectWithKeysSorted } from 'libs/end-to-end/src/copy-object-with-keys-sorted';
import { isEqual } from 'lodash';
import { FilterDefinitionForExtrasArray } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { determineFilterSetName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusAreaWithFilterSetNames } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getFeatureFromCustomVizId } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { resolveAnalysisNameForCustomViz } from 'libs/end-to-end/src/construct-body-display-of-custom-viz';
import { resolveFocusAreaForCustomViz } from 'libs/end-to-end/src/construct-body-display-of-custom-viz';
import { DynamicPipelineAnalysisConfiguration } from 'libs/end-to-end/src/dynamic-pipeline-analysis-configuration';
import { v4 as uuid } from 'uuid';
import { getDatabaseRevisionsForUser } from 'libs/end-to-end/src/get-database-revisions-for-user';
import { MyUser } from 'libs/end-to-end/src/my-user';

export const isCustomVizRequestDerivedSafelyFromDashboard = ({
  dashboardWithCharts,
  customVizRequest,
  databaseRevisionId,
}: {
  dashboardWithCharts: DashboardRecordDto;
  databaseRevisionId: string;
  customVizRequest: CustomVizRequest;
}): boolean => {
  if (
    customVizRequest.customVizId === 'topLevelAnalysis' &&
    customVizRequest.organizationId === dashboardWithCharts.organizationId &&
    customVizRequest.observationLocationType.databaseId ===
      dashboardWithCharts.databaseId &&
    customVizRequest.observationLocationType.databaseRevisionId ===
      databaseRevisionId
  ) {
    return true;
  }

  for (const chart of dashboardWithCharts.charts) {
    if (
      isCustomVizRequestDerivedSafelyFromDashboardSingle({
        globalFiltersOriginal: dashboardWithCharts.globalFilters || [],
        customVizRequest,
        chartCustomVizRequest: chart.customVizRequest,
      })
    ) {
      return true;
    }
  }

  return false;
};

export const isCustomVizRequestDerivedSafelyFromDashboardSingle = ({
  globalFiltersOriginal,
  customVizRequest,
  chartCustomVizRequest,
}: {
  globalFiltersOriginal: FilterDefinitionForExtrasArray;
  customVizRequest: CustomVizRequest;
  chartCustomVizRequest: CustomVizRequest;
}) => {
  if (
    !chartCustomVizRequest ||
    chartCustomVizRequest.customVizId !== customVizRequest.customVizId ||
    chartCustomVizRequest.organizationId !== customVizRequest.organizationId ||
    chartCustomVizRequest.observationLocationType.databaseId !==
      customVizRequest.observationLocationType.databaseId ||
    chartCustomVizRequest.observationLocationType.databaseRevisionId !==
      customVizRequest.observationLocationType.databaseRevisionId
  ) {
    return false;
  }

  const displayOnlyKeys: Record<CustomVizId, string[]> = {
    payBenchmarking: [],
    hiringCopilot: [],
    promotionCopilot: [],
    payCorrection: [],
    biasAnalysis: ['treatmentDimensionLevel'],
    binScatterMultiSelect: [],
    binScatterCrossSelect: [],
    pipelineAnalysis: [],
    successPhenotypeV2: [],
    topLevelAnalysis: [],
    progress: [],
    heatmap: ['treatmentDimensionLevel', 'dimension', 'filterDefinition'],
    nlpGraph: [],
    tableView: ['visibleDepartments'],
    timeSeriesTrends: ['chartType'],
    summaryStats: [],
    richText: [],
    geolocation: [],
    externalStreamlit: [],
  };

  const inputDropdowns = copyObjectWithKeysSorted(
    customVizRequest.inputs.dropdowns,
  );
  const chartDropdowns = copyObjectWithKeysSorted(
    chartCustomVizRequest.inputs.dropdowns,
  );

  // The dropdowns must match exactly.
  if (!isEqual(inputDropdowns, chartDropdowns)) {
    return false;
  }

  const inputExtras = copyObjectWithKeysSorted(customVizRequest.inputs.extras);
  const chartExtras = copyObjectWithKeysSorted(
    chartCustomVizRequest.inputs.extras,
  );
  const extraKeysToIgnoreInInitialComparison: string[] = [
    'filters',
    ...(displayOnlyKeys[customVizRequest.customVizId] || []),
  ];

  for (const key of Object.keys(inputExtras)) {
    if (extraKeysToIgnoreInInitialComparison.includes(key)) {
      continue;
    }

    const aUnk: unknown =
      typeof inputExtras === 'object' && key in inputExtras
        ? (inputExtras as Record<string, unknown>)[key]
        : undefined;
    const bUnk: unknown =
      typeof chartExtras === 'object' && key in chartExtras
        ? (chartExtras as Record<string, unknown>)[key]
        : undefined;

    if (!isEqual(aUnk, bUnk)) {
      return false;
    }
  }

  // Special handling for filters, we want to make sure that their submission is a subset of the dashboard's global filters (if specified).
  const globalFiltersCopy = copyObjectWithKeysSorted({
    globalFilters: globalFiltersOriginal,
  });
  const globalFilters = globalFiltersCopy.globalFilters;
  const inputExtrasFilters =
    'filters' in inputExtras ? inputExtras.filters || [] : [];
  const chartExtrasFilters =
    'filters' in chartExtras ? chartExtras.filters || [] : [];

  if (globalFilters.length === 0) {
    // There are no global filters, so let's compare them normally instead.
    return isEqual(inputExtrasFilters, chartExtrasFilters) !== false;
  }

  // For each of the global filters, make sure that the input extra filters are well-behaved.
  for (const globalFilter of globalFilters) {
    const matchingInputFilter = inputExtrasFilters.find((f) => {
      return f.columnHeaderValue === globalFilter.columnHeaderValue;
    });

    if (!matchingInputFilter) {
      // The input filters do not include a filter for this global filter.
      return false;
    }

    // If a global filter has no values, then there is something wrong and we should just reject it.
    if (globalFilter.values.length === 0) {
      return false;
    }

    // Are the values from matchingInputFilter a subset of globalFilter values?
    const isSubset = matchingInputFilter.values.every((value) =>
      globalFilter.values.includes(value),
    );

    if (!isSubset) {
      return false;
    }
  }

  // For each of the chart filters, make sure that the input extra filters are well behaved (skipping those already checked against global filters).
  for (const chartFilter of chartExtrasFilters) {
    // If this filter is also in the global filters, we've already checked it.
    const isInGlobalFilters = globalFilters.some((f) => {
      return f.columnHeaderValue === chartFilter.columnHeaderValue;
    });

    if (isInGlobalFilters) {
      continue;
    }

    const matchingInputFilter = inputExtrasFilters.find((f) => {
      return f.columnHeaderValue === chartFilter.columnHeaderValue;
    });

    if (!matchingInputFilter) {
      // The input filters do not include a filter for this chart filter.
      return false;
    }

    // Are the values from matchingInputFilter identical to chartFilter values?
    if (!isEqual(matchingInputFilter.values, chartFilter.values)) {
      return false;
    }
  }

  // Only if we passed all checks do we return true.
  return true;
};

export const normalizeChartVizRequestForGlobalFilters = ({
  customVizRequest,
  globalFilters,
  filterSetDefinitionsWithValues,
  focusAreas,
  pipelineAnalysisConfiguration,
  databaseRevisionId,
  organizationId,
  databaseId,
}: {
  customVizRequest: CustomVizRequest;
  globalFilters: FilterDefinitionForExtrasArray;

  /**
   * These come from the schema of the revision
   */
  filterSetDefinitionsWithValues: FilterSetDefinitionWithValues[];
  focusAreas: FocusAreaWithFilterSetNames[];
  pipelineAnalysisConfiguration:
    | DynamicPipelineAnalysisConfiguration[]
    | undefined;
  databaseRevisionId: string;
  organizationId: string;
  databaseId: string;
}): CustomVizRequest => {
  const feature = getFeatureFromCustomVizId({
    customVizId: customVizRequest.customVizId,
  });

  // If we can't determine the feature, we can't determine the filter set name, so just return the original request.
  if (!feature) {
    return customVizRequest;
  }

  const focusArea = resolveFocusAreaForCustomViz(customVizRequest);
  const analysisName = resolveAnalysisNameForCustomViz(customVizRequest);

  const filterSetName = determineFilterSetName({
    feature,
    focusAreas,
    focusArea,
    analysisName,
    filterSetDefinitionsWithValues,
    pipelineAnalysisConfiguration,
  });

  const filterDefinitions =
    filterSetDefinitionsWithValues.find(
      (fs) => fs.filterSetName === filterSetName,
    )?.filterDefinitions ?? [];

  // Create a copy since we mutate before returning.
  const newCustomVizRequest = CustomVizRequest.parse(customVizRequest);

  // Mutation is okay since we made a copy above.
  newCustomVizRequest.organizationId = organizationId;
  newCustomVizRequest.observationLocationType = {
    type: 'db-revision',
    databaseId,
    databaseRevisionId,
  };

  if ('filters' in newCustomVizRequest.inputs.extras) {
    const existingFilters = newCustomVizRequest.inputs.extras.filters;

    const updatedFilters: FilterDefinitionForExtrasArray = [];

    // Any filter that exists in the filter set should be copied from the global filters into the custom viz request.
    // Leave the rest of the filters in the request alone.
    for (const filterDef of filterDefinitions) {
      const matchingGlobalFilter = globalFilters.find(
        (gf) => gf.columnHeaderValue === filterDef.columnHeaderValue,
      );

      if (matchingGlobalFilter) {
        updatedFilters.push(matchingGlobalFilter);
      }
    }

    // Now add any existing filters that were not part of the filter set.
    for (const existingFilter of existingFilters) {
      const isAlreadyIncluded = updatedFilters.some(
        (uf) => uf.columnHeaderValue === existingFilter.columnHeaderValue,
      );

      if (!isAlreadyIncluded) {
        updatedFilters.push(existingFilter);
      }
    }

    // Mutation here is okay since we made a copy above.
    newCustomVizRequest.inputs.extras.filters = updatedFilters;
  }

  return newCustomVizRequest;
};

export const convertDashboardRecordToDraft = (
  dashboard: DashboardRecordDto,
): DraftDashboard => {
  return {
    name: dashboard.name,
    charts: dashboard.charts.map((c) => ({
      ...c,
      draftId: uuid(),
      positionOnDashboard: c.positionOnDashboard,
      positionOnRow: c.positionOnRow,
      width: c.width,
      height: c.height,
    })),
    isPublic: dashboard.isPublic,
    globalFilters: dashboard.globalFilters,
    databaseRevisionId: dashboard.databaseRevisionId,
  };
};

export const updateGlobalFiltersInDraft = ({
  draft,
  globalFilters,
}: {
  draft: DraftDashboard;
  globalFilters: FilterDefinitionForExtrasArray | null;
}): DraftDashboard => {
  return {
    ...draft,
    globalFilters,
  };
};

export const updateChartInDraft = ({
  draft,
  chart,
}: {
  draft: DraftDashboard;
  chart: DraftDashboardChart;
}): DraftDashboard => {
  return {
    ...draft,
    charts: draft.charts.map((c) => (c.draftId === chart.draftId ? chart : c)),
  };
};

export const determineEffectiveDatabaseRevisionIdForDashboard = ({
  databaseId,
  myUser,
  dashboardDatabaseRevisionId,
}: {
  dashboardDatabaseRevisionId: string | null;
  databaseId: string;
  myUser: MyUser | null;
}) => {
  if (dashboardDatabaseRevisionId) {
    return dashboardDatabaseRevisionId;
  }

  const revisions = getDatabaseRevisionsForUser({
    myUser,
    databaseId,
  });

  if (revisions.length === 0) {
    return null;
  }

  for (const revision of revisions) {
    if (!revision.isHidden) {
      return revision.value;
    }
  }

  return null;
};
